import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Typography,
} from '@mui/material';

interface SquareImageCropModalProps {
  open: boolean;
  file: File | null;
  onCancel: () => void;
  onComplete: (file: File, previewUrl?: string) => void;
}

interface CropState {
  x: number;
  y: number;
  size: number;
}

const MIN_SIZE = 500;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const SquareImageCropModal: React.FC<SquareImageCropModalProps> = ({
  open,
  file,
  onCancel,
  onComplete,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>(
    { width: 0, height: 0 }
  );
  const [error, setError] = useState<string>('');
  const [crop, setCrop] = useState<CropState>({ x: 0, y: 0, size: MIN_SIZE });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!file) {
      setPreviewUrl('');
      setImageSize({ width: 0, height: 0 });
      setError('');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const url = typeof reader.result === 'string' ? reader.result : '';
      setPreviewUrl(url);
      const img = new Image();
      img.onload = () => {
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        if (!width || !height) {
          setError('Impossible de lire les dimensions de l’image.');
          return;
        }
        if (width < MIN_SIZE || height < MIN_SIZE) {
          setError('L’image doit mesurer au minimum 500 x 500 pixels.');
          return;
        }
        const minDim = Math.min(width, height);
        const initialSize = clamp(minDim, MIN_SIZE, minDim);
        setImageSize({ width, height });
        setCrop({
          x: (width - initialSize) / 2,
          y: (height - initialSize) / 2,
          size: initialSize,
        });
        setError('');
      };
      img.onerror = () => {
        setError('Impossible de charger l’image.');
      };
      img.src = url;
      imageRef.current = img;
    };
    reader.readAsDataURL(file);

    return () => {
      if (previewUrl) {
        setPreviewUrl('');
      }
    };
  }, [file, open]);

  const displayDimensions = useMemo(() => {
    if (!imageSize.width || !imageSize.height) {
      return { width: 0, height: 0, scale: 1 };
    }
    const maxWidth = 520;
    const scale = Math.min(1, maxWidth / imageSize.width);
    return {
      width: imageSize.width * scale,
      height: imageSize.height * scale,
      scale,
    };
  }, [imageSize]);

  const handleMoveCrop = (clientX: number, clientY: number) => {
    const container = containerRef.current;
    if (!container || !displayDimensions.scale) {
      return;
    }
    const rect = container.getBoundingClientRect();
    const relX = (clientX - rect.left) / displayDimensions.scale;
    const relY = (clientY - rect.top) / displayDimensions.scale;
    const nextX = clamp(relX - crop.size / 2, 0, imageSize.width - crop.size);
    const nextY = clamp(relY - crop.size / 2, 0, imageSize.height - crop.size);
    setCrop((prev) => ({ ...prev, x: nextX, y: nextY }));
  };

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (error) {
      return;
    }
    setIsDragging(true);
    handleMoveCrop(event.clientX, event.clientY);
  };

  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (!isDragging) {
      return;
    }
    handleMoveCrop(event.clientX, event.clientY);
  };

  const handlePointerUp: React.PointerEventHandler<HTMLDivElement> = () => {
    setIsDragging(false);
  };

  const handleSizeChange = (_event: Event, value: number | number[]) => {
    if (typeof value !== 'number') {
      return;
    }
    const minDim = Math.min(imageSize.width, imageSize.height);
    const newSize = clamp(value, MIN_SIZE, minDim);
    setCrop((prev) => {
      const nextX = clamp(prev.x, 0, imageSize.width - newSize);
      const nextY = clamp(prev.y, 0, imageSize.height - newSize);
      return { x: nextX, y: nextY, size: newSize };
    });
  };

  const handleCropConfirm = async () => {
    if (!file || !imageRef.current || !crop.size || error) {
      return;
    }
    const canvas = document.createElement('canvas');
    const outputSize = Math.max(MIN_SIZE, Math.round(crop.size));
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Impossible de préparer la zone de découpe.');
      return;
    }
    ctx.drawImage(
      imageRef.current,
      crop.x,
      crop.y,
      crop.size,
      crop.size,
      0,
      0,
      outputSize,
      outputSize
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('La génération de l’image recadrée a échoué.');
          return;
        }
        const croppedFile = new File([blob], file.name || 'image.jpg', {
          type: file.type || 'image/jpeg',
        });
        const nextPreviewUrl = canvas.toDataURL(file.type || 'image/jpeg');
        onComplete(croppedFile, nextPreviewUrl);
      },
      file.type || 'image/jpeg'
    );
  };

  const showCropArea = Boolean(previewUrl && !error && displayDimensions.width && displayDimensions.height);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      sx={{
        // Keep the crop modal above the Bootstrap "Add Product" modal.
        zIndex: 2600,
      }}
    >
      <DialogTitle>Recadrer l’image en carré</DialogTitle>
      <DialogContent>
        {error ? (
          <Typography color="error" sx={{ my: 1 }}>
            {error}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Déplacez le cadre pour choisir la zone à conserver. La zone recadrée sera en carré et
            mesurera au moins 500 x 500 pixels.
          </Typography>
        )}
        {showCropArea && (
          <Stack spacing={2} alignItems="center">
            <Box
              ref={containerRef}
              sx={{
                position: 'relative',
                width: displayDimensions.width,
                height: displayDimensions.height,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid #ddd',
                touchAction: 'none',
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            >
              <img
                src={previewUrl}
                alt="Prévisualisation"
                style={{
                  width: displayDimensions.width,
                  height: displayDimensions.height,
                  display: 'block',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: crop.x * displayDimensions.scale,
                  top: crop.y * displayDimensions.scale,
                  width: crop.size * displayDimensions.scale,
                  height: crop.size * displayDimensions.scale,
                  border: '2px solid #1976d2',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)',
                  cursor: 'move',
                }}
              />
            </Box>
            <Box sx={{ width: '100%', maxWidth: 460 }}>
              <Typography gutterBottom>Taille du cadre</Typography>
              <Slider
                value={crop.size}
                min={MIN_SIZE}
                max={Math.min(imageSize.width, imageSize.height)}
                onChange={handleSizeChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${Math.round(value)} px`}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Annuler</Button>
        <Button
          variant="contained"
          onClick={handleCropConfirm}
          disabled={Boolean(error) || !showCropArea}
        >
          Valider le recadrage
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SquareImageCropModal;
